// AdminApplications.jsx - Place in /workspaces/mcn-youtube-platform/mcn-frontend/src/components/AdminApplications.jsx

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL as API_URL } from '../config';
import './AdminApplications.css';

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    niche: 'all',
    dateRange: '30'
  });
  const [reviewData, setReviewData] = useState({
    overallScore: 5,
    contentQuality: 5,
    channelPotential: 5,
    experienceLevel: 5,
    equipmentQuality: 5,
    riskLevel: 'LOW',
    notes: '',
    decision: ''
  });

  useEffect(() => {
    fetchApplications();
  }, [filters]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/applications/admin/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: filters
      });
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = (application) => {
    setSelectedApplication(application);
    setShowReviewModal(true);
  };

  const submitReview = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/applications/admin/review/${selectedApplication.id}`,
        reviewData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setApplications(prev => prev.map(app => 
        app.id === selectedApplication.id 
          ? { ...app, status: reviewData.decision === 'approve' ? 'APPROVED' : 'REJECTED' }
          : app
      ));
      
      setShowReviewModal(false);
      setSelectedApplication(null);
      setReviewData({
        overallScore: 5,
        contentQuality: 5,
        channelPotential: 5,
        experienceLevel: 5,
        equipmentQuality: 5,
        riskLevel: 'LOW',
        notes: '',
        decision: ''
      });
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'PENDING_REVIEW': { class: 'badge-warning', text: 'Pending Review' },
      'UNDER_REVIEW': { class: 'badge-info', text: 'Under Review' },
      'APPROVED': { class: 'badge-success', text: 'Approved' },
      'REJECTED': { class: 'badge-danger', text: 'Rejected' },
      'ON_HOLD': { class: 'badge-secondary', text: 'On Hold' }
    };
    return badges[status] || { class: 'badge-default', text: status };
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="loading">Loading applications...</div>;
  }

  return (
    <div className="admin-applications">
      <div className="applications-header">
        <h2>Creator Applications</h2>
        <div className="filters">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="PENDING_REVIEW">Pending Review</option>
            <option value="UNDER_REVIEW">Under Review</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
          
          <select
            value={filters.niche}
            onChange={(e) => setFilters({ ...filters, niche: e.target.value })}
            className="filter-select"
          >
            <option value="all">All Niches</option>
            <option value="Technology">Technology</option>
            <option value="Gaming">Gaming</option>
            <option value="Education">Education</option>
            <option value="Entertainment">Entertainment</option>
            <option value="Music">Music</option>
            <option value="Sports">Sports</option>
            <option value="Food">Food & Cooking</option>
            <option value="Travel">Travel</option>
            <option value="Fashion">Fashion & Beauty</option>
            <option value="Health">Health & Fitness</option>
          </select>
          
          <select
            value={filters.dateRange}
            onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
            className="filter-select"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      <div className="applications-stats">
        <div className="stat-card">
          <div className="stat-value">{applications.length}</div>
          <div className="stat-label">Total Applications</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {applications.filter(a => a.status === 'PENDING_REVIEW').length}
          </div>
          <div className="stat-label">Pending Review</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {applications.filter(a => a.status === 'APPROVED').length}
          </div>
          <div className="stat-label">Approved</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {applications.filter(a => a.status === 'REJECTED').length}
          </div>
          <div className="stat-label">Rejected</div>
        </div>
      </div>

      <div className="applications-table">
        <table>
          <thead>
            <tr>
              <th>Applicant</th>
              <th>Channel</th>
              <th>Niche</th>
              <th>Subscribers</th>
              <th>Experience</th>
              <th>Status</th>
              <th>Applied</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {applications.map(app => (
              <tr key={app.id}>
                <td>
                  <div className="applicant-info">
                    <div className="name">{app.firstName} {app.lastName}</div>
                    <div className="email">{app.email}</div>
                  </div>
                </td>
                <td>
                  <div className="channel-info">
                    <div className="channel-name">{app.channelName}</div>
                    <a href={app.channelUrl} target="_blank" rel="noopener noreferrer" className="channel-link">
                      View Channel →
                    </a>
                  </div>
                </td>
                <td>{app.contentNiche}</td>
                <td>{app.currentSubscribers?.toLocaleString() || '0'}</td>
                <td>{app.yearsOfExperience || 0} years</td>
                <td>
                  <span className={`badge ${getStatusBadge(app.status).class}`}>
                    {getStatusBadge(app.status).text}
                  </span>
                </td>
                <td>{formatDate(app.submittedAt)}</td>
                <td>
                  <button
                    className="btn-review"
                    onClick={() => handleReview(app)}
                    disabled={app.status !== 'PENDING_REVIEW' && app.status !== 'UNDER_REVIEW'}
                  >
                    Review
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {applications.length === 0 && (
          <div className="no-data">
            No applications found matching your filters.
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedApplication && (
        <div className="modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Review Application</h3>
              <button className="modal-close" onClick={() => setShowReviewModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="review-applicant-info">
                <h4>{selectedApplication.firstName} {selectedApplication.lastName}</h4>
                <p>{selectedApplication.email}</p>
                <p>Channel: {selectedApplication.channelName}</p>
                <p>Subscribers: {selectedApplication.currentSubscribers?.toLocaleString()}</p>
              </div>
              
              <div className="review-form">
                <div className="review-scores">
                  <div className="score-item">
                    <label>Content Quality (1-10)</label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={reviewData.contentQuality}
                      onChange={(e) => setReviewData({...reviewData, contentQuality: parseInt(e.target.value)})}
                    />
                    <span className="score-value">{reviewData.contentQuality}</span>
                  </div>
                  
                  <div className="score-item">
                    <label>Channel Potential (1-10)</label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={reviewData.channelPotential}
                      onChange={(e) => setReviewData({...reviewData, channelPotential: parseInt(e.target.value)})}
                    />
                    <span className="score-value">{reviewData.channelPotential}</span>
                  </div>
                  
                  <div className="score-item">
                    <label>Experience Level (1-10)</label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={reviewData.experienceLevel}
                      onChange={(e) => setReviewData({...reviewData, experienceLevel: parseInt(e.target.value)})}
                    />
                    <span className="score-value">{reviewData.experienceLevel}</span>
                  </div>
                  
                  <div className="score-item">
                    <label>Equipment Quality (1-10)</label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={reviewData.equipmentQuality}
                      onChange={(e) => setReviewData({...reviewData, equipmentQuality: parseInt(e.target.value)})}
                    />
                    <span className="score-value">{reviewData.equipmentQuality}</span>
                  </div>
                </div>
                
                <div className="review-risk">
                  <label>Risk Level</label>
                  <select
                    value={reviewData.riskLevel}
                    onChange={(e) => setReviewData({...reviewData, riskLevel: e.target.value})}
                  >
                    <option value="LOW">Low Risk</option>
                    <option value="MEDIUM">Medium Risk</option>
                    <option value="HIGH">High Risk</option>
                  </select>
                </div>
                
                <div className="review-notes">
                  <label>Review Notes</label>
                  <textarea
                    value={reviewData.notes}
                    onChange={(e) => setReviewData({...reviewData, notes: e.target.value})}
                    placeholder="Add your review notes here..."
                    rows="4"
                  />
                </div>
                
                <div className="review-decision">
                  <button
                    className="btn-approve"
                    onClick={() => {
                      setReviewData({...reviewData, decision: 'approve'});
                      submitReview();
                    }}
                  >
                    Approve Application
                  </button>
                  <button
                    className="btn-reject"
                    onClick={() => {
                      setReviewData({...reviewData, decision: 'reject'});
                      submitReview();
                    }}
                  >
                    Reject Application
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminApplications;